import { Injectable } from '@angular/core';
import { ofType, Actions, createEffect } from '@ngrx/effects';
import { mergeMap, map, catchError, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import {
  AdminSkillsErroredAction,
  AdminDeleteSkillSuccessAction,
  GoToSkillDefinitionAction,
  AdminCreateSkillAction,
  AdminUpdateSkillAction,
  AdminDeleteSkillAction,
  AdminSkillsUsageLoadedAction,
  AdminLoadSkillsUsageAction,
  AdminSkillsLoadedAction,
  AdminLoadSkillsAction,
} from './admin-skills.action';
import { Router } from '@angular/router';
import { getSkillsErrorType } from '../skills/helpers/skills-error.util';
import { AdminSkillApiError, AdminSkillApiErrorTypes } from './admin-skills.errors';
import { AdminSkillService } from './admin-skills.service';

@Injectable()
export class AdminSkillsEffects {
  loadSkills$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminLoadSkillsAction),
      mergeMap(() =>
        this.skillService.loadSkills().pipe(
          map((skills) => AdminSkillsLoadedAction({ skills })),
          catchError((err) =>
            of(
              AdminSkillsErroredAction({
                error: new AdminSkillApiError(
                  getSkillsErrorType(AdminSkillApiErrorTypes, err.status),
                  err,
                ),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  createSkill$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminCreateSkillAction),
      mergeMap((action) =>
        this.skillService.createSkill(action.values).pipe(
          map(() => GoToSkillDefinitionAction()),
          catchError((err) =>
            of(
              AdminSkillsErroredAction({
                error: new AdminSkillApiError(
                  getSkillsErrorType(AdminSkillApiErrorTypes, err.status),
                  err,
                ),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  updateSkill$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminUpdateSkillAction),
      mergeMap((action) =>
        this.skillService.updateSkill(action.values, action.id).pipe(
          map(() => GoToSkillDefinitionAction()),
          catchError((err) =>
            of(
              AdminSkillsErroredAction({
                error: new AdminSkillApiError(
                  getSkillsErrorType(AdminSkillApiErrorTypes, err.status),
                  err,
                ),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  deleteSkill$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminDeleteSkillAction),
      mergeMap((action) =>
        this.skillService.deleteSkill(action.id).pipe(
          map(() => AdminDeleteSkillSuccessAction({ id: action.id })),
          catchError((err) =>
            of(
              AdminSkillsErroredAction({
                error: new AdminSkillApiError(
                  getSkillsErrorType(AdminSkillApiErrorTypes, err.status),
                  err,
                ),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  goToSkillDefinition$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(GoToSkillDefinitionAction),
        tap(() => {
          this.router.navigate(['/admin', 'skills']);
        }),
      ),
    { dispatch: false },
  );

  getSkillsUsage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminLoadSkillsUsageAction),
      mergeMap(() =>
        this.skillService.getSkillsUsage().pipe(
          map((skillsUsage) => AdminSkillsUsageLoadedAction({ skillsUsage })),
          catchError((err) =>
            of(
              AdminSkillsErroredAction({
                error: new AdminSkillApiError(
                  getSkillsErrorType(AdminSkillApiErrorTypes, err.status),
                  err,
                ),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  constructor(
    private actions$: Actions,
    private skillService: AdminSkillService,
    private router: Router,
  ) {}
}
